// src/pages/help/helpContent.js
// Contenu des guides du centre d'aide, une entrée par slug de helpTopics.js.
// Chaque guide est une page autonome : intro, « en bref », étapes, astuces, FAQ.
//
// Deux niveaux :
//  - topics/<slug>.js  = contenu VALIDÉ (fait foi, à éditer directement)
//  - DRAFT_FR/EN ici   = brouillon déduit du code, en attendant la validation
// Les brouillons sont écrasés par les contenus validés au moment de l'export.
import * as faqGenerale from "./topics/faqGenerale";
import * as agentIaMt5 from "./topics/agentIaMt5";
import * as chatIa from "./topics/chatIa";
import * as journalAuto from "./topics/journalAuto";
import * as executionTopic from "./topics/execution";
import * as copyTrading from "./topics/copyTrading";
import * as challenges from "./topics/challenges";
import * as strategyHub from "./topics/strategyHub";
import * as analyseFondamentale from "./topics/analyseFondamentale";
import * as graphiquesBacktest from "./topics/graphiquesBacktest";
import * as autres from "./topics/autres";
import * as abonnementPaiements from "./topics/abonnementPaiements";

const VALIDATED = {
  "faq-generale": faqGenerale,
  "agent-ia-mt5": agentIaMt5,
  "chat-ia": chatIa,
  "journal-auto": journalAuto,
  execution: executionTopic,
  "copy-trading": copyTrading,
  challenges,
  "strategy-hub": strategyHub,
  "analyse-fondamentale": analyseFondamentale,
  "graphiques-backtest": graphiquesBacktest,
  autres,
  "abonnement-paiements": abonnementPaiements,
};

const DRAFT_FR = {
  "faq-generale": {
    title: "FAQ générale",
    tagline: "Réponses aux questions les plus courantes",
    readMin: 5,
    intro:
      "FullMetrix est la plateforme premium de FullMargin : l'espace avancé qui réunit le Chat IA, le Strategy Hub, le journal automatique, l'exécution d'ordres, le copy trading et le backtest, tous alimentés par vos données MetaTrader 5.",
    brief: [
      "Comprendre ce qu'est FullMetrix et à qui il s'adresse",
      "Savoir ce que la plateforme fait — et ce qu'elle ne fait pas",
      "Trouver rapidement le bon guide pour chaque module",
    ],
    steps: [
      {
        title: "Installez l'Agent IA sur MetaTrader 5",
        text: "C'est le point de départ : sans agent, la plupart des modules restent en lecture seule.",
      },
      {
        title: "Vérifiez que votre compte est synchronisé",
        text: "Broker, numéro de compte et capital doivent apparaître dans la page Agent IA.",
      },
      {
        title: "Explorez module par module",
        text: "Journal auto pour vos données, Exécution pour agir, Strategy Hub pour structurer, Backtest pour tester.",
      },
    ],
    tips: [
      "FullMetrix ne donne pas de signaux : il fournit du contexte et des analyses, la décision reste la vôtre.",
      "Le compte est unique : identifiants, profil et abonnement se gèrent sur FullMargin.",
    ],
    faq: [
      {
        q: "Qu'est-ce que FullMetrix ?",
        a:
          "FullMetrix est la plateforme premium de FullMargin : l'espace avancé qui donne accès à la pleine puissance des outils FullMargin, enrichis par l'IA, pour l'analyse, l'exécution et l'optimisation.\n\nFullMetrix regroupe :\n• un Chat IA spécialisé pour les traders\n• un Strategy Hub\n• une plateforme Graphique & Backtest avancée\n• un Agent IA MetaTrader 5 pour centraliser et exploiter vos données.",
      },
      {
        q: "À qui s'adresse FullMetrix ?",
        a:
          "• aux débutants qui veulent structurer leur apprentissage\n• aux intermédiaires qui veulent corriger leurs erreurs\n• aux avancés qui veulent analyser leurs données, tester leurs stratégies et automatiser leur gestion\n\n👉 La plateforme aide à prendre de meilleures décisions — elle ne décide pas à votre place.",
      },
      {
        q: "À quoi sert le Chat IA de FullMetrix ?",
        a:
          "Le Chat IA est un assistant spécialisé en trading, relié à votre Agent IA.\n\nIl permet :\n• questions marché\n• analyse de performances\n• compréhension d'erreurs\n• lectures contextualisées\n• interaction avec votre compte (si l'Agent est installé)\n\n👉 Ce n'est pas un simple chatbot : il est connecté à vos données.",
      },
      {
        q: "Qu'est-ce que le Strategy Hub ?",
        a:
          "Le Strategy Hub sert à :\n• créer et documenter vos stratégies\n• structurer les règles d'entrée / gestion / sortie\n• obtenir une analyse IA (cohérence, timing, risque, discipline)\n\n👉 L'objectif est de renforcer votre stratégie, pas de « donner une stratégie ».",
      },
      {
        q: "À quoi sert la partie Graphique & Backtest ?",
        a:
          "Plateforme avancée d'analyse qui permet :\n• indices synthétiques (Deriv)\n• Forex\n• analyses IA directement sur le graphique\n• outils avancés : Replay, multi-timeframe, contexte (volatilité, range, tendance)\n\n👉 Elle sert à comprendre le marché avant d'agir, pas à suivre des signaux.",
      },
      {
        q: "Qu'est-ce que l'Agent IA (MT5) ?",
        a:
          "Un agent à installer sur MetaTrader 5 pour relier votre compte à FullMetrix.\n\nIl permet :\n• l'export automatique des données MT5\n• un journal de trading basé sur des données réelles\n• l'analyse de votre historique (gains, pertes, erreurs, régularité)\n\n👉 C'est le cœur data de FullMetrix.",
      },
      {
        q: "À quoi sert le journal de trading ?",
        a:
          "Il est généré automatiquement depuis MT5.\n\nIl permet :\n• l'analyse des performances réelles\n• l'identification d'erreurs récurrentes\n• la personnalisation (tags, commentaires, catégories)\n\n👉 Vous passez d'un trading « ressenti » à un trading basé sur des données.",
      },
      {
        q: "Puis-je trader depuis FullMetrix ?",
        a:
          "Oui, via l'Agent IA :\n• ouvrir des positions\n• gérer plusieurs positions\n• outils avancés : SL/TP en masse, trailing, calcul de lots, gestion du risque\n\n👉 FullMetrix devient un centre de contrôle.",
      },
      {
        q: "FullMetrix donne-t-il des signaux ?",
        a:
          "❌ Non.\n\n✔️ FullMetrix fournit du contexte, des analyses IA et des outils d'aide à la décision.\n\n👉 Vous restez responsable de vos décisions.",
      },
      {
        q: "FullMetrix est-il automatisé ?",
        a:
          "Assisté par IA, pas entièrement automatisé.\n\nL'IA analyse, synthétise et contextualise — le trader décide et exécute.\n\n👉 Objectif : autonomie, pas dépendance.",
      },
      {
        q: "D'autres outils à l'avenir ?",
        a:
          "Oui, la plateforme évolue en continu.\n\nOutils en cours :\n• multi-comptes\n• discipline\n• gestion du risque\n• optimisation des performances\n• analyses institutionnelles et fondamentales.",
      },
    ],
    related: ["agent-ia-mt5", "chat-ia", "abonnement-paiements"],
  },

  "agent-ia-mt5": {
    title: "Agent IA MT5",
    tagline: "Relier votre compte MetaTrader 5 à FullMetrix",
    readMin: 6,
    intro:
      "L'Agent IA est un Expert Advisor (FullMetrixAgent.ex5) qui tourne dans MetaTrader 5. Il envoie vos données de compte à FullMetrix et exécute les ordres demandés depuis la plateforme. C'est le prérequis du journal automatique, de l'exécution, des challenges et du copy trading.",
    brief: [
      "Installer l'agent dans MetaTrader 5 et autoriser la connexion",
      "Coller votre Agent Token pour lier le compte",
      "Vérifier la synchronisation (broker, serveur, compte, capital)",
    ],
    steps: [
      {
        title: "Téléchargez l'agent",
        text: "Depuis la page Agent IA, bouton « Télécharger l'agent » : vous récupérez le fichier FullMetrixAgent.ex5.",
      },
      {
        title: "Copiez le fichier dans MT5",
        text: "MetaTrader 5 → Fichier → Ouvrir le dossier des données → MQL5 → Experts. Déposez-y FullMetrixAgent.ex5.",
      },
      {
        title: "Redémarrez MetaTrader 5",
        text: "Fermez puis rouvrez MT5 pour qu'il détecte le nouvel agent dans le navigateur d'Experts.",
      },
      {
        title: "Autorisez la connexion",
        text: "Outils → Options → Expert Advisors : cochez « Autoriser les WebRequest » et ajoutez l'adresse https://ia.fullmargin.net/ à la liste.",
      },
      {
        title: "Collez votre Agent Token",
        text: "Copiez le token depuis la page Agent IA de FullMetrix, glissez l'agent sur un graphique et collez le token dans ses paramètres.",
      },
      {
        title: "Activez l'AutoTrading",
        text: "Le bouton AutoTrading de MT5 doit être vert, sinon l'agent reçoit les ordres mais ne peut pas les exécuter.",
      },
    ],
    tips: [
      "Agent muet ? Vérifiez dans l'ordre : WebRequest autorisé, URL exacte dans la liste, token collé, AutoTrading actif.",
      "Un agent = un compte MT5. Pour suivre plusieurs comptes, lancez un agent par compte.",
      "Laissez MetaTrader 5 ouvert : l'agent ne synchronise que lorsque le terminal tourne.",
    ],
    faq: [
      {
        q: "Où trouver mon Agent Token ?",
        a: "Dans la page Agent IA de FullMetrix, en haut. Il est lié à votre compte : ne le partagez pas.",
      },
      {
        q: "La connexion reste « En attente », pourquoi ?",
        a:
          "L'agent est lancé mais n'atteint pas FullMetrix. Dans 9 cas sur 10 il s'agit de l'URL WebRequest absente ou mal orthographiée dans Outils → Options → Expert Advisors.",
      },
      {
        q: "Mes données sont-elles envoyées en continu ?",
        a:
          "L'agent pousse régulièrement l'état du compte, les positions et l'historique tant que MT5 est ouvert. La date de dernière synchro est affichée sur la page Agent IA.",
      },
    ],
    related: ["journal-auto", "execution", "copy-trading"],
  },

  "chat-ia": {
    title: "Chat IA",
    tagline: "Votre copilote de trading, connecté à votre compte",
    readMin: 4,
    intro:
      "Le Chat IA est l'écran d'accueil de FullMetrix. Ce n'est pas un chatbot générique : quand l'Agent IA est installé, il lit vos positions, votre historique et votre journal pour répondre avec vos vraies données, et il peut vous emmener directement sur la bonne page.",
    brief: [
      "Poser des questions marché, performance ou méthode",
      "Faire analyser vos trades et votre journal",
      "Naviguer vers l'Agent, l'exécution, le backtest ou les stratégies",
    ],
    steps: [
      {
        title: "Écrivez votre question",
        text: "En langage naturel : « analyse mes 20 derniers trades », « pourquoi je perds sur les news ? », « explique le RSI ».",
      },
      {
        title: "Utilisez les raccourcis sous la barre",
        text: "Installer l'Agent IA, Mon journal automatique, Gérer mes positions et Lancer un backtest ouvrent directement la page concernée.",
      },
      {
        title: "Reliez votre compte pour aller plus loin",
        text: "Sans agent, le Chat répond de façon générale. Avec agent, il travaille sur vos données réelles.",
      },
    ],
    tips: [
      "Plus la question est précise (paire, période, type d'erreur), plus la réponse est exploitable.",
      "Le Chat peut se tromper : vérifiez toujours les informations critiques avant d'agir.",
    ],
    faq: [
      {
        q: "Le Chat peut-il passer un ordre ?",
        a:
          "Il vous amène sur la page Exécution avec le contexte. Le passage d'ordre se confirme depuis Exécution ou l'Agent IA, jamais en aveugle depuis la conversation.",
      },
      {
        q: "Mes conversations sont-elles conservées ?",
        a: "Oui, l'historique de vos échanges est rattaché à votre compte et se retrouve au fil des sessions.",
      },
      {
        q: "Pourquoi le Chat ne voit-il pas mes trades ?",
        a: "Aucun agent connecté, ou agent hors ligne. Ouvrez la page Agent IA et vérifiez la date de dernière synchro.",
      },
    ],
    related: ["agent-ia-mt5", "journal-auto", "analyse-fondamentale"],
  },

  "journal-auto": {
    title: "Journal automatique",
    tagline: "Votre journal de trading généré depuis MT5",
    readMin: 5,
    intro:
      "Le journal automatique reconstruit votre activité à partir des données réelles envoyées par l'Agent IA : trades clôturés, séries de pertes, drawdown, régularité. Objectif : passer d'un trading « au ressenti » à un trading mesuré.",
    brief: [
      "Voir vos trades clôturés et vos positions sans saisie manuelle",
      "Recevoir un audit : zones critiques, erreurs répétées, alertes",
      "Suivre les règles de discipline (pause après pertes, arrêt de journée)",
    ],
    steps: [
      {
        title: "Connectez l'Agent IA",
        text: "Sans agent actif, le journal n'a aucune donnée à analyser. Le bandeau du haut vous indique le token attendu.",
      },
      {
        title: "Laissez l'historique se remplir",
        text: "L'audit devient fiable une fois assez de trades clôturés remontés — avant, il affiche « Accumuler des données ».",
      },
      {
        title: "Lisez l'audit dans l'ordre",
        text: "D'abord les alertes prioritaires (drawdown, séries de pertes), puis les zones à corriger, enfin les points forts.",
      },
      {
        title: "Appliquez les règles proposées",
        text: "Pause obligatoire après pertes consécutives, arrêt de la session au-delà de la perte maximale prévue, pas d'augmentation de lot après une perte.",
      },
    ],
    tips: [
      "Une alerte drawdown est prioritaire sur tout le reste : protégez le capital avant de chercher à accélérer.",
      "Filtrez par symbole ou par sens pour isoler l'instrument qui plombe vos statistiques.",
    ],
    faq: [
      {
        q: "Pourquoi mon journal est-il vide ?",
        a: "Aucun AgentToken actif, ou aucun trade clôturé remonté. Enregistrez votre token en haut de la page, puis attendez une synchro.",
      },
      {
        q: "Puis-je annoter mes trades ?",
        a: "Oui : tags, commentaires et catégories viennent enrichir les données brutes remontées de MT5.",
      },
      {
        q: "L'audit remplace-t-il mon analyse ?",
        a: "Non. Il met en évidence des régularités statistiques ; l'interprétation et les corrections restent de votre ressort.",
      },
    ],
    related: ["agent-ia-mt5", "challenges", "strategy-hub"],
  },

  execution: {
    title: "Exécution des ordres",
    tagline: "Piloter votre compte MT5 depuis FullMetrix",
    readMin: 6,
    intro:
      "La page Exécution transforme FullMetrix en centre de contrôle : ouverture d'ordres au marché ou en attente, modification, clôture, et outils de gestion en masse (break-even, trailing, risk/reward). Tout passe par l'Agent IA installé sur MT5.",
    brief: [
      "Ouvrir un ordre marché ou en attente sur n'importe quel symbole du broker",
      "Gérer SL / TP, break-even et trailing sur plusieurs positions à la fois",
      "Suivre positions ouvertes, ordres en attente et historique",
    ],
    steps: [
      {
        title: "Vérifiez la configuration requise",
        text: "Un agent connecté et un token actif sont nécessaires pour envoyer des ordres. Le bandeau le rappelle si ce n'est pas le cas.",
      },
      {
        title: "Choisissez le type d'exécution",
        text: "Les onglets de la barre latérale : Aperçu, Marché, En attente, Modifier, Fermer, Break-even, Trailing, Risk / Reward, Builder.",
      },
      {
        title: "Sélectionnez le symbole",
        text: "Le champ de recherche interroge la liste réelle du broker. Si un symbole n'apparaît pas, il n'est pas disponible côté MT5.",
      },
      {
        title: "Réglez volume, SL et TP",
        text: "Le Risk / Reward et le calcul de lots aident à dimensionner selon le risque accepté, pas au hasard.",
      },
      {
        title: "Envoyez et vérifiez",
        text: "La table des positions affiche ticket, symbole, sens, volume, prix d'ouverture, SL, TP et P&L. Toute erreur MT5 est remontée telle quelle.",
      },
    ],
    tips: [
      "Break-even et Trailing s'appliquent en masse : vérifiez la sélection avant d'exécuter.",
      "Si l'AutoTrading est coupé dans MT5, l'ordre part mais n'est jamais exécuté.",
    ],
    faq: [
      {
        q: "Mes ordres partent mais rien ne se passe",
        a: "Vérifiez l'AutoTrading de MT5 (bouton vert), et que le terminal est ouvert. L'agent ne peut rien exécuter terminal fermé.",
      },
      {
        q: "Puis-je fermer plusieurs positions d'un coup ?",
        a: "Oui, l'onglet Fermer permet une clôture groupée, par symbole ou par sens.",
      },
      {
        q: "Le P&L affiché est-il temps réel ?",
        a: "Il suit le rythme de synchronisation de l'agent. En cas de doute, la référence reste MetaTrader 5.",
      },
    ],
    related: ["agent-ia-mt5", "copy-trading", "journal-auto"],
  },

  "copy-trading": {
    title: "Copy Trading",
    tagline: "Copier un master, ou devenir master",
    readMin: 5,
    intro:
      "Le copy trading relie deux comptes MT5 via leurs agents : le master émet ses opérations, le copieur les reproduit. Chaque master publie ses statistiques (ROI 30 jours, drawdown max, nombre de trades, followers) pour que le choix se fasse sur des chiffres.",
    brief: [
      "Comparer les masters sur ROI, drawdown, trades et followers",
      "Connecter votre compte en copieur via votre token",
      "Passer master et exposer vos performances",
    ],
    steps: [
      {
        title: "Connectez votre agent",
        text: "Master comme copieur ont besoin d'un agent actif. Sans connexion, la page reste en mode démo.",
      },
      {
        title: "Choisissez un master",
        text: "Triez par récents, followers, ROI, trades, drawdown ou nom, puis ouvrez le détail pour voir la courbe de ROI sur 30 jours.",
      },
      {
        title: "Générez et collez votre token",
        text: "Le lien entre les deux comptes se fait par token. Tant qu'il n'est pas généré, le statut reste « Token non généré ».",
      },
      {
        title: "Vérifiez le statut de connexion",
        text: "« Copieur connecté » ou « Master connecté » confirme que le lien est actif ; « En attente de l'agent » signale que MT5 ne répond pas.",
      },
    ],
    tips: [
      "Regardez le drawdown max avant le ROI : un ROI élevé avec un DD profond est un profil risqué.",
      "Certains masters sont affichés en vitrine mais ne sont pas copiables — le message « Master indisponible » l'indique.",
      "Le copy trading ne réduit pas le risque : dimensionnez votre capital en conséquence.",
    ],
    faq: [
      {
        q: "Puis-je copier plusieurs masters ?",
        a: "Le lien se fait compte par compte. Pour diversifier, il faut un compte MT5 (et un agent) par master copié.",
      },
      {
        q: "Que se passe-t-il si mon terminal se ferme ?",
        a: "La copie s'arrête. Les positions déjà ouvertes restent en place mais ne sont plus suivies tant que l'agent est hors ligne.",
      },
      {
        q: "Comment devenir master ?",
        a: "Activez le mode master depuis la page Copy Trading ; le statut passe de « Master non activé » à « Master connecté » une fois l'agent lié.",
      },
    ],
    related: ["agent-ia-mt5", "execution", "abonnement-paiements"],
  },

  challenges: {
    title: "Challenges",
    tagline: "Fixer un objectif et suivre la progression",
    readMin: 4,
    intro:
      "Un challenge est un objectif chiffré sur une durée donnée : un montant cible ou un pourcentage de gain, suivi automatiquement grâce au capital synchronisé par votre agent. Le suivi est factuel — pas de saisie manuelle, donc pas d'auto-complaisance.",
    brief: [
      "Créer un challenge avec objectif et échéance",
      "Suivre la progression jour par jour en temps réel",
      "Mesurer votre taux de réussite sur l'ensemble des challenges",
    ],
    steps: [
      {
        title: "Connectez votre compte agent",
        text: "Le capital de départ et la progression viennent de l'agent : broker, compte, capital et dernière synchro s'affichent en haut.",
      },
      {
        title: "Créez un challenge",
        text: "Bouton « Nouveau challenge » : définissez un montant cible ou un objectif en pourcentage, et une durée en jours.",
      },
      {
        title: "Suivez la progression",
        text: "Chaque carte affiche départ, objectif, actuel, gain et le compteur « Jour X / Y ».",
      },
      {
        title: "Lisez le bilan",
        text: "La vue d'ensemble récapitule actifs, réussis, en retard, progression moyenne, profit total et taux de réussite.",
      },
    ],
    tips: [
      "Un challenge « En retard » n'est pas perdu : c'est un signal pour ajuster le rythme, pas pour doubler les lots.",
      "Fixez des objectifs atteignables : l'intérêt est la régularité mesurée, pas le record.",
    ],
    faq: [
      {
        q: "La progression ne bouge pas",
        a: "Le capital vient de l'agent. Si la dernière synchro est ancienne, ouvrez MT5 et vérifiez que l'agent tourne.",
      },
      {
        q: "Puis-je annuler un challenge ?",
        a: "Oui, son statut passe alors à « Annulé » et il sort du calcul du taux de réussite en cours.",
      },
      {
        q: "Plusieurs challenges en parallèle ?",
        a: "Oui. La vue d'ensemble agrège tous les challenges actifs du compte.",
      },
    ],
    related: ["journal-auto", "agent-ia-mt5", "strategy-hub"],
  },

  "strategy-hub": {
    title: "Strategy Hub",
    tagline: "Structurer vos stratégies et les faire noter par l'IA",
    readMin: 5,
    intro:
      "Le Strategy Hub est votre laboratoire : vous décrivez une stratégie (contexte, règles d'entrée, gestion, sortie, money management), l'IA l'évalue et renvoie un score sur 10 avec les points faibles et les pistes d'amélioration. Le but est de renforcer votre méthode, pas de vous en fournir une.",
    brief: [
      "Documenter chaque stratégie dans un format constant",
      "Obtenir une analyse IA : cohérence, timing, risque, discipline",
      "Comparer les versions et garder vos favorites sous la main",
    ],
    steps: [
      {
        title: "Créez une stratégie",
        text: "Nommez-la, puis remplissez les rubriques : contexte de marché, conditions d'entrée, gestion en cours de trade, conditions de sortie, money management.",
      },
      {
        title: "Lancez l'analyse IA",
        text: "Une fois les champs remplis, lancez l'analyse depuis la fiche. Le score et le niveau s'affichent avec la date de dernière mise à jour.",
      },
      {
        title: "Lisez le détail",
        text: "La vue détaillée développe rubrique par rubrique : incohérences, faiblesses, recommandations concrètes.",
      },
      {
        title: "Itérez",
        text: "Corrigez, relancez l'analyse, comparez les scores. Marquez en favori les versions que vous utilisez réellement.",
      },
    ],
    tips: [
      "Une stratégie incomplète donne une analyse pauvre : remplissez la gestion du risque, c'est la rubrique la plus discriminante.",
      "Confrontez le score IA à votre journal automatique : c'est là que se voit l'écart entre la règle et la pratique.",
    ],
    faq: [
      {
        q: "« Aucune analyse disponible », pourquoi ?",
        a: "Les informations de la stratégie ne sont pas assez complètes. Complétez la fiche, puis relancez l'analyse.",
      },
      {
        q: "L'IA peut-elle me donner une stratégie ?",
        a: "Non. Elle évalue et améliore ce que vous écrivez ; elle ne fournit pas de système clé en main.",
      },
      {
        q: "Mes stratégies sont-elles privées ?",
        a: "Oui, elles sont rattachées à votre compte FullMetrix.",
      },
    ],
    related: ["graphiques-backtest", "journal-auto", "chat-ia"],
  },

  "analyse-fondamentale": {
    title: "Analyse fondamentale",
    tagline: "Macro, calendrier économique et biais de marché",
    readMin: 5,
    intro:
      "Le module d'analyse relie le contexte macro à votre instrument : calendrier économique des 7 prochains jours, biais calculé par instrument (différentiel de taux, inflation), régime de marché et aide au dimensionnement selon la volatilité. Il complète l'analyse IA lancée sur un symbole ou un sujet.",
    brief: [
      "Repérer les événements à impact avant qu'ils ne tombent",
      "Lire un biais synthétique par instrument, avec son niveau de confiance",
      "Dimensionner la position selon le régime de volatilité",
    ],
    steps: [
      {
        title: "Choisissez l'instrument",
        text: "Le biais, le régime et le sizing sont recalculés pour l'instrument sélectionné.",
      },
      {
        title: "Commencez par le calendrier",
        text: "Filtrez par impact (faible, moyen, élevé) et basculez UTC ou heure locale pour ne pas vous tromper d'horaire.",
      },
      {
        title: "Lisez le biais",
        text: "Biais global, différentiel de taux (en points de pourcentage), variation d'inflation sur 3 mois, et courbe du score sur 7 jours.",
      },
      {
        title: "Dimensionnez",
        text: "Le KPI volatilité & sizing traduit le régime en taille de position raisonnable.",
      },
      {
        title: "Lancez une analyse IA",
        text: "Sur un symbole (EURUSD, BTCUSD…) ou un sujet (inflation, taux BCE), en choisissant le type : générale, technique ou fondamentale.",
      },
    ],
    tips: [
      "Un biais fort avec une confiance faible ne vaut pas un biais moyen bien confirmé : lisez toujours les deux.",
      "Les analyses sont générées automatiquement et ne constituent pas un conseil en investissement.",
    ],
    faq: [
      {
        q: "« Flux LIVE indisponible »",
        a: "La source de données ne répond pas. Rafraîchissez ; si cela persiste, l'analyse reste consultable sur les dernières valeurs connues.",
      },
      {
        q: "Puis-je suivre plusieurs instruments ?",
        a: "Oui, la liste de suivi garde vos favoris pour les rappeler en un clic.",
      },
      {
        q: "À quelle fréquence le calendrier est-il mis à jour ?",
        a: "En continu sur une fenêtre glissante de 7 jours ; la date de dernière mise à jour est affichée sur chaque bloc.",
      },
    ],
    related: ["graphiques-backtest", "chat-ia", "strategy-hub"],
  },

  "graphiques-backtest": {
    title: "Graphiques & Backtest",
    tagline: "Tester vos stratégies sur données historiques, dans MT5",
    readMin: 5,
    intro:
      "Le backtest FullMetrix ne tourne pas dans le navigateur : c'est un outil MetaTrader 5 que votre abonnement débloque. Vous testez sur données historiques réelles, avec replay et multi-timeframe, sans faire ramer votre machine côté web.",
    brief: [
      "Récupérer l'outil et activer votre licence",
      "L'installer dans MT5 comme l'Agent IA",
      "Rejouer l'historique et mesurer une stratégie avant de la trader",
    ],
    steps: [
      {
        title: "Vérifiez votre licence",
        text: "Elle s'active automatiquement dès que l'abonnement FullMetrix est actif. Le bloc « Ta licence » affiche Active ou Inactive.",
      },
      {
        title: "Téléchargez l'outil",
        text: "Bouton de téléchargement de la page Backtest (abonnement requis).",
      },
      {
        title: "Installez-le dans MetaTrader 5",
        text: "Dossier MQL5/Experts, puis redémarrez MT5 et glissez l'outil sur un graphique.",
      },
      {
        title: "Collez votre licence",
        text: "Elle vous est demandée au premier lancement ; copiez-la depuis la page Backtest.",
      },
      {
        title: "Lancez le backtest",
        text: "Replay, multi-timeframe et lecture du contexte (volatilité, range, tendance) pour comprendre, pas seulement pour compter.",
      },
    ],
    tips: [
      "Un backtest sur trop peu de trades ne prouve rien : cherchez la robustesse, pas la courbe parfaite.",
      "Les mises à jour de l'outil sont incluses tant que l'abonnement est actif.",
    ],
    faq: [
      {
        q: "Ma licence est « Inactive »",
        a: "L'abonnement FullMetrix n'est pas actif. Activez-le côté FullMargin : la licence suit automatiquement.",
      },
      {
        q: "Pourquoi l'outil n'est-il pas dans le navigateur ?",
        a: "Pour rester léger et fidèle : l'exécution dans MT5 utilise les vraies données du broker sans charger le navigateur.",
      },
      {
        q: "Compatible copy trading ?",
        a: "Oui, l'outil cohabite avec l'agent et le copy trading sur le même terminal.",
      },
    ],
    related: ["abonnement-paiements", "strategy-hub", "agent-ia-mt5"],
  },

  "abonnement-paiements": {
    title: "Abonnement & Paiements",
    tagline: "Gérer votre plan depuis votre compte FullMargin",
    readMin: 3,
    intro:
      "FullMetrix et FullMargin partagent le même compte. Profil, facturation, moyens de paiement et abonnement se gèrent donc sur FullMargin ; FullMetrix lit simplement l'état de votre plan pour débloquer les modules et la licence du backtest.",
    brief: [
      "Savoir où gérer profil, abonnement et facturation",
      "Comprendre ce que l'abonnement débloque",
      "Réagir quand un module se bloque après un changement de plan",
    ],
    steps: [
      {
        title: "Ouvrez votre profil FullMargin",
        text: "Menu du compte, en haut à droite → « Mon compte ». Vous êtes redirigé vers votre profil FullMargin.",
      },
      {
        title: "Gérez le plan et le paiement",
        text: "Changement de formule, moyen de paiement et historique de facturation se font côté FullMargin.",
      },
      {
        title: "Revenez sur FullMetrix",
        text: "L'état du plan est relu à la connexion suivante ; la licence du backtest s'active automatiquement.",
      },
    ],
    tips: [
      "Un module qui redevient verrouillé après un changement de plan : déconnectez-vous puis reconnectez-vous pour forcer la relecture.",
    ],
    faq: [
      {
        q: "Pourquoi n'y a-t-il plus de page compte dans FullMetrix ?",
        a: "Le compte est unique. Le dupliquer des deux côtés créait des réglages contradictoires : tout ce qui touche au profil est centralisé sur FullMargin.",
      },
      {
        q: "Ma licence backtest reste inactive après paiement",
        a: "Elle suit l'abonnement. S'il est actif côté FullMargin et que la licence ne suit pas, contactez l'assistance avec votre email de compte.",
      },
      {
        q: "Où changer mon mot de passe ?",
        a: "Sur FullMargin, dans votre profil. FullMetrix utilise la même connexion.",
      },
    ],
    related: ["graphiques-backtest", "abonnement-paiements", "faq-generale"],
  },
};

const DRAFT_EN = {
  "faq-generale": {
    title: "General FAQ",
    tagline: "Answers to the most common questions",
    readMin: 5,
    intro:
      "FullMetrix is the premium FullMargin platform: the advanced workspace bringing together AI Chat, Strategy Hub, automatic journal, order execution, copy trading and backtesting — all powered by your MetaTrader 5 data.",
    brief: [
      "Understand what FullMetrix is and who it is for",
      "Know what the platform does — and what it does not",
      "Quickly find the right guide for each module",
    ],
    steps: [
      {
        title: "Install the AI Agent on MetaTrader 5",
        text: "This is the starting point: without the agent, most modules stay read-only.",
      },
      {
        title: "Check that your account is synced",
        text: "Broker, account number and balance must show up on the AI Agent page.",
      },
      {
        title: "Explore module by module",
        text: "Auto journal for your data, Execution to act, Strategy Hub to structure, Backtest to test.",
      },
    ],
    tips: [
      "FullMetrix gives no signals: it provides context and analysis — the decision stays yours.",
      "One single account: credentials, profile and subscription are managed on FullMargin.",
    ],
    faq: [
      {
        q: "What is FullMetrix?",
        a:
          "FullMetrix is the premium FullMargin platform: the advanced workspace giving full access to FullMargin tools, enhanced by AI, for analysis, execution and optimisation.\n\nIt includes:\n• an AI Chat built for traders\n• a Strategy Hub\n• an advanced Charting & Backtest platform\n• a MetaTrader 5 AI Agent to centralise and use your data.",
      },
      {
        q: "Who is FullMetrix for?",
        a:
          "• beginners who want to structure their learning\n• intermediate traders who want to fix their mistakes\n• advanced traders who want to analyse their data, test strategies and automate management\n\n👉 The platform helps you decide better — it does not decide for you.",
      },
      {
        q: "What is the FullMetrix AI Chat for?",
        a:
          "The AI Chat is a trading assistant connected to your AI Agent.\n\nIt handles:\n• market questions\n• performance analysis\n• understanding your mistakes\n• contextual readings\n• interaction with your account (if the Agent is installed)\n\n👉 Not a generic chatbot: it is connected to your data.",
      },
      {
        q: "What is the Strategy Hub?",
        a:
          "The Strategy Hub lets you:\n• create and document your strategies\n• structure entry / management / exit rules\n• get an AI review (consistency, timing, risk, discipline)\n\n👉 The goal is to strengthen your strategy, not to hand you one.",
      },
      {
        q: "What is Charting & Backtest for?",
        a:
          "An advanced analysis platform offering:\n• synthetic indices (Deriv)\n• Forex\n• AI analysis directly on the chart\n• advanced tools: Replay, multi-timeframe, context (volatility, range, trend)\n\n👉 It is there to understand the market before acting, not to follow signals.",
      },
      {
        q: "What is the AI Agent (MT5)?",
        a:
          "An agent installed on MetaTrader 5 to link your account to FullMetrix.\n\nIt provides:\n• automatic MT5 data export\n• a trading journal built on real data\n• analysis of your history (wins, losses, mistakes, consistency)\n\n👉 It is the data core of FullMetrix.",
      },
      {
        q: "What is the trading journal for?",
        a:
          "It is generated automatically from MT5.\n\nIt provides:\n• real performance analysis\n• detection of recurring mistakes\n• customisation (tags, comments, categories)\n\n👉 You move from gut-feel trading to data-based trading.",
      },
      {
        q: "Can I trade from FullMetrix?",
        a:
          "Yes, through the AI Agent:\n• open positions\n• manage several positions\n• advanced tools: bulk SL/TP, trailing, lot sizing, risk management\n\n👉 FullMetrix becomes a control centre.",
      },
      {
        q: "Does FullMetrix provide signals?",
        a: "❌ No.\n\n✔️ FullMetrix provides context, AI analysis and decision-support tools.\n\n👉 You remain responsible for your decisions.",
      },
      {
        q: "Is FullMetrix automated?",
        a:
          "AI-assisted, not fully automated.\n\nThe AI analyses, summarises and contextualises — the trader decides and executes.\n\n👉 Goal: autonomy, not dependency.",
      },
      {
        q: "More tools coming?",
        a:
          "Yes, the platform evolves continuously.\n\nIn progress:\n• multi-account\n• discipline\n• risk management\n• performance optimisation\n• institutional and fundamental analysis.",
      },
    ],
    related: ["agent-ia-mt5", "chat-ia", "abonnement-paiements"],
  },

  "agent-ia-mt5": {
    title: "MT5 AI Agent",
    tagline: "Connect your MetaTrader 5 account to FullMetrix",
    readMin: 6,
    intro:
      "The AI Agent is an Expert Advisor (FullMetrixAgent.ex5) running inside MetaTrader 5. It sends your account data to FullMetrix and executes the orders requested from the platform. It is required for the automatic journal, execution, challenges and copy trading.",
    brief: [
      "Install the agent in MetaTrader 5 and allow the connection",
      "Paste your Agent Token to link the account",
      "Check the sync (broker, server, account, balance)",
    ],
    steps: [
      { title: "Download the agent", text: "From the AI Agent page, “Download agent” button: you get the FullMetrixAgent.ex5 file." },
      {
        title: "Copy the file into MT5",
        text: "MetaTrader 5 → File → Open Data Folder → MQL5 → Experts. Drop FullMetrixAgent.ex5 there.",
      },
      { title: "Restart MetaTrader 5", text: "Close and reopen MT5 so it picks up the new agent in the Navigator." },
      {
        title: "Allow the connection",
        text: "Tools → Options → Expert Advisors: tick “Allow WebRequest” and add https://ia.fullmargin.net/ to the list.",
      },
      {
        title: "Paste your Agent Token",
        text: "Copy the token from the FullMetrix AI Agent page, drag the agent onto a chart and paste the token in its inputs.",
      },
      { title: "Enable AutoTrading", text: "The MT5 AutoTrading button must be green, otherwise orders arrive but cannot be executed." },
    ],
    tips: [
      "Silent agent? Check in order: WebRequest allowed, exact URL in the list, token pasted, AutoTrading on.",
      "One agent = one MT5 account. To follow several accounts, run one agent per account.",
      "Keep MetaTrader 5 open: the agent only syncs while the terminal runs.",
    ],
    faq: [
      { q: "Where do I find my Agent Token?", a: "On the FullMetrix AI Agent page, at the top. It is tied to your account — never share it." },
      {
        q: "Connection stays “Pending”, why?",
        a: "The agent runs but cannot reach FullMetrix. In most cases the WebRequest URL is missing or mistyped in Tools → Options → Expert Advisors.",
      },
      {
        q: "Is my data sent continuously?",
        a: "The agent pushes account state, positions and history regularly while MT5 is open. The last sync time is shown on the AI Agent page.",
      },
    ],
    related: ["journal-auto", "execution", "copy-trading"],
  },

  "chat-ia": {
    title: "AI Chat",
    tagline: "Your trading copilot, connected to your account",
    readMin: 4,
    intro:
      "The AI Chat is the FullMetrix home screen. Not a generic chatbot: when the AI Agent is installed it reads your positions, history and journal to answer with your real data, and it can take you straight to the right page.",
    brief: [
      "Ask market, performance or method questions",
      "Have your trades and journal analysed",
      "Jump to the Agent, execution, backtest or strategies",
    ],
    steps: [
      { title: "Type your question", text: "In plain language: “review my last 20 trades”, “why do I lose on news?”, “explain RSI”." },
      {
        title: "Use the shortcuts under the bar",
        text: "Install the AI Agent, My automatic journal, Manage my positions and Run a backtest open the matching page directly.",
      },
      { title: "Connect your account to go further", text: "Without an agent the Chat answers generically. With one, it works on your real data." },
    ],
    tips: [
      "The more precise the question (pair, period, type of mistake), the more usable the answer.",
      "The Chat can be wrong: always verify critical information before acting.",
    ],
    faq: [
      {
        q: "Can the Chat place an order?",
        a: "It takes you to the Execution page with the context. Orders are confirmed from Execution or the AI Agent, never blindly from the conversation.",
      },
      { q: "Are my conversations kept?", a: "Yes, your history is attached to your account and persists across sessions." },
      { q: "Why can't the Chat see my trades?", a: "No agent connected, or the agent is offline. Open the AI Agent page and check the last sync." },
    ],
    related: ["agent-ia-mt5", "journal-auto", "analyse-fondamentale"],
  },

  "journal-auto": {
    title: "Automatic journal",
    tagline: "Your trading journal generated from MT5",
    readMin: 5,
    intro:
      "The automatic journal rebuilds your activity from the real data sent by the AI Agent: closed trades, losing streaks, drawdown, consistency. The point is to move from gut-feel trading to measured trading.",
    brief: [
      "See your closed trades and positions with no manual entry",
      "Get an audit: critical areas, repeated mistakes, alerts",
      "Follow discipline rules (break after losses, stop for the day)",
    ],
    steps: [
      { title: "Connect the AI Agent", text: "Without an active agent the journal has nothing to analyse. The top banner shows the expected token." },
      {
        title: "Let history build up",
        text: "The audit becomes reliable once enough closed trades are in — before that it shows “collecting data”.",
      },
      {
        title: "Read the audit in order",
        text: "Priority alerts first (drawdown, losing streaks), then areas to fix, then strengths.",
      },
      {
        title: "Apply the suggested rules",
        text: "Mandatory break after consecutive losses, stop the session past the planned max loss, no lot increase after a loss.",
      },
    ],
    tips: [
      "A drawdown alert outranks everything else: protect capital before trying to accelerate.",
      "Filter by symbol or side to isolate the instrument dragging your stats down.",
    ],
    faq: [
      { q: "Why is my journal empty?", a: "No active Agent Token, or no closed trades yet. Save your token at the top of the page, then wait for a sync." },
      { q: "Can I annotate my trades?", a: "Yes: tags, comments and categories enrich the raw MT5 data." },
      { q: "Does the audit replace my own analysis?", a: "No. It surfaces statistical patterns; interpretation and fixes remain yours." },
    ],
    related: ["agent-ia-mt5", "challenges", "strategy-hub"],
  },

  execution: {
    title: "Order execution",
    tagline: "Drive your MT5 account from FullMetrix",
    readMin: 6,
    intro:
      "The Execution page turns FullMetrix into a control centre: market or pending orders, modification, closing, and bulk management tools (break-even, trailing, risk/reward). Everything goes through the AI Agent installed on MT5.",
    brief: [
      "Open a market or pending order on any broker symbol",
      "Manage SL / TP, break-even and trailing across several positions",
      "Track open positions, pending orders and history",
    ],
    steps: [
      { title: "Check the requirements", text: "A connected agent and an active token are required to send orders. The banner reminds you if not." },
      {
        title: "Pick the execution type",
        text: "Sidebar tabs: Overview, Market, Pending, Modify, Close, Break-even, Trailing, Risk / Reward, Builder.",
      },
      { title: "Select the symbol", text: "The search field queries the broker's real list. A missing symbol simply is not available on MT5." },
      { title: "Set volume, SL and TP", text: "Risk / Reward and lot sizing help you size against accepted risk instead of guessing." },
      {
        title: "Send and verify",
        text: "The positions table shows ticket, symbol, side, volume, open price, SL, TP and P&L. Any MT5 error is reported as-is.",
      },
    ],
    tips: [
      "Break-even and Trailing apply in bulk: check the selection before executing.",
      "If AutoTrading is off in MT5 the order is sent but never executed.",
    ],
    faq: [
      { q: "Orders go out but nothing happens", a: "Check MT5 AutoTrading (green button) and that the terminal is open. The agent cannot execute with the terminal closed." },
      { q: "Can I close several positions at once?", a: "Yes, the Close tab supports bulk closing by symbol or side." },
      { q: "Is the P&L real time?", a: "It follows the agent's sync rate. When in doubt, MetaTrader 5 remains the reference." },
    ],
    related: ["agent-ia-mt5", "copy-trading", "journal-auto"],
  },

  "copy-trading": {
    title: "Copy Trading",
    tagline: "Copy a master, or become one",
    readMin: 5,
    intro:
      "Copy trading links two MT5 accounts through their agents: the master emits trades, the copier reproduces them. Each master publishes stats (30-day ROI, max drawdown, trades, followers) so the choice is made on numbers.",
    brief: [
      "Compare masters on ROI, drawdown, trades and followers",
      "Connect your account as a copier using your token",
      "Become a master and expose your performance",
    ],
    steps: [
      { title: "Connect your agent", text: "Both master and copier need an active agent. Without a connection the page stays in demo mode." },
      {
        title: "Pick a master",
        text: "Sort by recent, followers, ROI, trades, drawdown or name, then open the detail view for the 30-day ROI curve.",
      },
      { title: "Generate and paste your token", text: "The link between accounts is made by token. Until then the status stays “Token not generated”." },
      {
        title: "Check the connection status",
        text: "“Copier connected” or “Master connected” confirms the link; “Waiting for agent” means MT5 is not responding.",
      },
    ],
    tips: [
      "Look at max drawdown before ROI: a high ROI with a deep DD is a risky profile.",
      "Some masters are showcased but not copyable — the “Master unavailable” message says so.",
      "Copy trading does not reduce risk: size your capital accordingly.",
    ],
    faq: [
      { q: "Can I copy several masters?", a: "The link is per account. To diversify you need one MT5 account (and agent) per copied master." },
      { q: "What if my terminal closes?", a: "Copying stops. Already open positions stay but are no longer tracked while the agent is offline." },
      { q: "How do I become a master?", a: "Enable master mode from the Copy Trading page; the status moves from “Master not enabled” to “Master connected” once the agent is linked." },
    ],
    related: ["agent-ia-mt5", "execution", "abonnement-paiements"],
  },

  challenges: {
    title: "Challenges",
    tagline: "Set a target and track progress",
    readMin: 4,
    intro:
      "A challenge is a numeric target over a set duration: an amount or a percentage gain, tracked automatically from the balance synced by your agent. Tracking is factual — no manual entry, so no self-indulgence.",
    brief: ["Create a challenge with a target and a deadline", "Track progress day by day in real time", "Measure your success rate across challenges"],
    steps: [
      { title: "Connect your agent account", text: "Starting balance and progress come from the agent: broker, account, balance and last sync show at the top." },
      { title: "Create a challenge", text: "“New challenge” button: set a target amount or a percentage goal, and a duration in days." },
      { title: "Track progress", text: "Each card shows start, target, current, gain and the “Day X / Y” counter." },
      { title: "Read the summary", text: "The overview sums up active, successful, late, average progress, total profit and success rate." },
    ],
    tips: [
      "A “Late” challenge is not lost: it is a signal to adjust pace, not to double lot size.",
      "Set reachable targets: the value is measured consistency, not a record.",
    ],
    faq: [
      { q: "Progress is not moving", a: "Balance comes from the agent. If the last sync is old, open MT5 and check the agent is running." },
      { q: "Can I cancel a challenge?", a: "Yes, its status becomes “Cancelled” and it leaves the running success-rate calculation." },
      { q: "Several challenges at once?", a: "Yes. The overview aggregates all active challenges on the account." },
    ],
    related: ["journal-auto", "agent-ia-mt5", "strategy-hub"],
  },

  "strategy-hub": {
    title: "Strategy Hub",
    tagline: "Structure your strategies and have AI score them",
    readMin: 5,
    intro:
      "The Strategy Hub is your lab: you describe a strategy (context, entry rules, management, exit, money management), the AI reviews it and returns a score out of 10 with weaknesses and improvement paths. The goal is to strengthen your method, not to hand you one.",
    brief: ["Document each strategy in a consistent format", "Get an AI review: consistency, timing, risk, discipline", "Compare versions and keep favourites at hand"],
    steps: [
      {
        title: "Create a strategy",
        text: "Name it, then fill each section: market context, entry conditions, in-trade management, exit conditions, money management.",
      },
      { title: "Run the AI review", text: "Once filled, run the review from the strategy sheet. Score and level appear with the last update date." },
      { title: "Read the detail", text: "The detailed view expands section by section: inconsistencies, weaknesses, concrete recommendations." },
      { title: "Iterate", text: "Fix, re-run, compare scores. Mark as favourite the versions you actually trade." },
    ],
    tips: [
      "An incomplete strategy gets a poor review: fill in risk management, it is the most decisive section.",
      "Compare the AI score with your automatic journal: that is where rule versus practice shows up.",
    ],
    faq: [
      { q: "“No analysis available”, why?", a: "The strategy information is not complete enough. Fill in the sheet, then run the review again." },
      { q: "Can the AI give me a strategy?", a: "No. It reviews and improves what you write; it does not provide a turnkey system." },
      { q: "Are my strategies private?", a: "Yes, they are tied to your FullMetrix account." },
    ],
    related: ["graphiques-backtest", "journal-auto", "chat-ia"],
  },

  "analyse-fondamentale": {
    title: "Fundamental analysis",
    tagline: "Macro, economic calendar and market bias",
    readMin: 5,
    intro:
      "The analysis module links macro context to your instrument: 7-day economic calendar, per-instrument bias (rate differential, inflation), market regime and volatility-based sizing. It complements the AI analysis run on a symbol or a topic.",
    brief: ["Spot high-impact events before they land", "Read a synthetic bias per instrument, with its confidence", "Size the position against the volatility regime"],
    steps: [
      { title: "Pick the instrument", text: "Bias, regime and sizing are recomputed for the selected instrument." },
      { title: "Start with the calendar", text: "Filter by impact (low, medium, high) and switch UTC or local time so you never miss a release." },
      {
        title: "Read the bias",
        text: "Global bias, rate differential (in percentage points), 3-month inflation change, and the 7-day score curve.",
      },
      { title: "Size it", text: "The volatility & sizing KPI turns the regime into a reasonable position size." },
      {
        title: "Run an AI analysis",
        text: "On a symbol (EURUSD, BTCUSD…) or a topic (inflation, ECB rates), choosing the type: general, technical or fundamental.",
      },
    ],
    tips: [
      "A strong bias with low confidence is worth less than a moderate, well-confirmed one: always read both.",
      "Analyses are generated automatically and are not investment advice.",
    ],
    faq: [
      { q: "“LIVE feed unavailable”", a: "The data source is not responding. Refresh; if it persists, the analysis stays available on the last known values." },
      { q: "Can I follow several instruments?", a: "Yes, the watchlist keeps your favourites one click away." },
      { q: "How often is the calendar updated?", a: "Continuously over a rolling 7-day window; the last update time is shown on each block." },
    ],
    related: ["graphiques-backtest", "chat-ia", "strategy-hub"],
  },

  "graphiques-backtest": {
    title: "Charting & Backtest",
    tagline: "Test your strategies on historical data, inside MT5",
    readMin: 5,
    intro:
      "The FullMetrix backtest does not run in the browser: it is a MetaTrader 5 tool unlocked by your subscription. You test on real historical data, with replay and multi-timeframe, without slowing your machine down on the web side.",
    brief: ["Get the tool and activate your licence", "Install it in MT5 like the AI Agent", "Replay history and measure a strategy before trading it"],
    steps: [
      { title: "Check your licence", text: "It activates automatically as soon as the FullMetrix subscription is active. The “Your licence” block shows Active or Inactive." },
      { title: "Download the tool", text: "Download button on the Backtest page (subscription required)." },
      { title: "Install it in MetaTrader 5", text: "MQL5/Experts folder, then restart MT5 and drag the tool onto a chart." },
      { title: "Paste your licence", text: "It is requested on first launch; copy it from the Backtest page." },
      { title: "Run the backtest", text: "Replay, multi-timeframe and context reading (volatility, range, trend) to understand, not only to count." },
    ],
    tips: [
      "A backtest on too few trades proves nothing: look for robustness, not a perfect curve.",
      "Tool updates are included while the subscription is active.",
    ],
    faq: [
      { q: "My licence is “Inactive”", a: "The FullMetrix subscription is not active. Activate it on FullMargin: the licence follows automatically." },
      { q: "Why isn't the tool in the browser?", a: "To stay light and faithful: running in MT5 uses real broker data without loading the browser." },
      { q: "Compatible with copy trading?", a: "Yes, the tool runs alongside the agent and copy trading on the same terminal." },
    ],
    related: ["abonnement-paiements", "strategy-hub", "agent-ia-mt5"],
  },

  "abonnement-paiements": {
    title: "Subscription & Payments",
    tagline: "Manage your plan from your FullMargin account",
    readMin: 3,
    intro:
      "FullMetrix and FullMargin share the same account. Profile, billing, payment methods and subscription are therefore managed on FullMargin; FullMetrix simply reads your plan state to unlock modules and the backtest licence.",
    brief: ["Know where to manage profile, subscription and billing", "Understand what the subscription unlocks", "React when a module locks after a plan change"],
    steps: [
      { title: "Open your FullMargin profile", text: "Account menu, top right → “My account”. You are redirected to your FullMargin profile." },
      { title: "Manage plan and payment", text: "Plan changes, payment method and billing history happen on FullMargin." },
      { title: "Come back to FullMetrix", text: "Plan state is re-read on the next login; the backtest licence activates automatically." },
    ],
    tips: ["A module still locked after a plan change: log out and back in to force a re-read."],
    faq: [
      {
        q: "Why is there no account page in FullMetrix any more?",
        a: "The account is single. Duplicating it on both sides created conflicting settings: everything profile-related is centralised on FullMargin.",
      },
      { q: "My backtest licence stays inactive after payment", a: "It follows the subscription. If it is active on FullMargin and the licence does not follow, contact support with your account email." },
      { q: "Where do I change my password?", a: "On FullMargin, in your profile. FullMetrix uses the same login." },
    ],
    related: ["graphiques-backtest", "abonnement-paiements", "faq-generale"],
  },
};

/* --------------------------------------------------------------------------
 * Assemblage : le contenu validé (topics/<slug>.js) prime sur le brouillon.
 * ------------------------------------------------------------------------ */
function merge(draft, lang) {
  const out = { ...draft };
  for (const [slug, mod] of Object.entries(VALIDATED)) {
    const validated = mod?.[lang];
    if (validated) out[slug] = validated;
  }
  return out;
}

export const HELP_CONTENT_FR = merge(DRAFT_FR, "fr");
export const HELP_CONTENT_EN = merge(DRAFT_EN, "en");
